export const SETTING_TYPES = [
  "VatRate",
  "DefaultCurrency",
  "MaintenanceMode",
  "MaxResourcesPerTenant",
  "DefaultBillingFrequency",
  "SmtpHost",
  "SmtpPort",
  "SmtpUsername",
  "SmtpPassword",
  "SmtpFromEmail",
  "SmtpFromName",
  "SmtpUseSsl",
  "StripeEnabled",
  "StripePublishableKey",
  "DomainRegistrationEnabled",
  "DatabaseManagementEnabled",
  "ZoneDisplayEnabled",
  "DnsManagementEnabled",
  "ObjectStorageEnabled",
  "FirewallEnabled",
  "MonitoringEnabled",
  "SupportWidgetEnabled",
  "EInvoiceEnabled",
  "VmDeletionEnabled",
  "VmSuspendEnabled",
  "CustomFeatureFlag",
  "AgreementSignupCommitmentEnabled",
  "AgreementVmCreationCommitmentEnabled",
  "VmReinstallConfigCheckEnabled",
  "AgreementSignupCommitment",
  "AgreementVmCreationCommitment",
  "PaymentBankTransferBankName",
  "PaymentBankTransferAccountNumber",
  "PaymentBankTransferAccountName",
  "PaymentBankTransferBranch",
  "PaymentBankTransferSwiftCode",
  "PaymentBankTransferDeployedSignature",
  "VmCpuLimitRatio",
  "VmUpgradeEnabled",
] as const;
export type SettingType = (typeof SETTING_TYPES)[number];

export const SETTING_TYPE_LABELS: Record<SettingType, string> = {
  VatRate: "VAT Rate",
  DefaultCurrency: "Default Currency",
  MaintenanceMode: "Maintenance Mode",
  MaxResourcesPerTenant: "Max Resources / Tenant",
  DefaultBillingFrequency: "Default Billing Frequency",
  SmtpHost: "SMTP Host",
  SmtpPort: "SMTP Port",
  SmtpUsername: "SMTP Username",
  SmtpPassword: "SMTP Password",
  SmtpFromEmail: "SMTP From Email",
  SmtpFromName: "SMTP From Name",
  SmtpUseSsl: "SMTP Use SSL",
  StripeEnabled: "Stripe Enabled",
  StripePublishableKey: "Stripe Publishable Key",
  DomainRegistrationEnabled: "Domain Registration Enabled",
  DatabaseManagementEnabled: "Database Management Enabled",
  ZoneDisplayEnabled: "Zone Display Enabled",
  DnsManagementEnabled: "DNS Management Enabled",
  ObjectStorageEnabled: "Object Storage Enabled",
  FirewallEnabled: "Firewall Enabled",
  MonitoringEnabled: "Monitoring Enabled",
  SupportWidgetEnabled: "Support Widget Enabled",
  EInvoiceEnabled: "E-invoice Enabled",
  VmDeletionEnabled: "VM Deletion Enabled",
  VmSuspendEnabled: "VM Suspend Enabled",
  CustomFeatureFlag: "Custom Feature Flag",
  AgreementSignupCommitmentEnabled: "Agreement — Sign-up Enabled",
  AgreementVmCreationCommitmentEnabled: "Agreement — VM Creation Enabled",
  VmReinstallConfigCheckEnabled: "VM Reinstall — Configuration Check Enabled",
  AgreementSignupCommitment: "Agreement — Sign-up Commitment",
  AgreementVmCreationCommitment: "Agreement — VM Creation Commitment",
  PaymentBankTransferBankName: "Bank Transfer Bank Name",
  PaymentBankTransferAccountNumber: "Bank Transfer Account Number",
  PaymentBankTransferAccountName: "Bank Transfer Account Name",
  PaymentBankTransferBranch: "Bank Transfer Branch",
  PaymentBankTransferSwiftCode: "Bank Transfer SWIFT Code",
  PaymentBankTransferDeployedSignature: "Bank Transfer Deployed Signature",
  VmCpuLimitRatio: "VM CPU Limit Ratio",
  VmUpgradeEnabled: "VM Upgrade Enabled",
};

/**
 * Feature-flag setting types. These are boolean toggles backed by a
 * PlatformSetting row whose Value is "true"/"false" ( IsActive must stay true —
 * see isFeatureFlagEnabled / the FeatureFlagsTab). They must never expose a
 * standalone IsActive switch in the CRUD UI.
 */
export const FEATURE_FLAG_TYPES = [
  "DomainRegistrationEnabled",
  "DatabaseManagementEnabled",
  "ZoneDisplayEnabled",
  "DnsManagementEnabled",
  "ObjectStorageEnabled",
  "FirewallEnabled",
  "MonitoringEnabled",
  "SupportWidgetEnabled",
  "EInvoiceEnabled",
  "VmDeletionEnabled",
  "VmSuspendEnabled",
  "VmUpgradeEnabled",
  "VmReinstallConfigCheckEnabled",
] as const;
export type FeatureFlagType = (typeof FEATURE_FLAG_TYPES)[number];

export const isFeatureFlagType = (t: string): t is FeatureFlagType =>
  (FEATURE_FLAG_TYPES as readonly string[]).includes(t);

/**
 * Registry describing each feature flag for the dedicated Feature Flags tab.
 * NOTE: for feature flags the registry `key` equals the SettingType string
 * (PascalCase). The API is queried by `type` (not `key`) because the backend
 * Key column stores a snake_case slug (e.g. "domain_registration_enabled") and
 * the backend FeatureFlagService resolves flags by Type.
 *
 * To add a new built-in flag (backend: WiCloud.Application/Common/Constants/
 * FeatureFlagRegistry.cs carries the same checklist):
 * 1. Backend — add a SettingType member, an entry in FeatureFlagRegistry.KnownFlags,
 *    an IsXEnabledAsync on IFeatureFlagService/FeatureFlagService, and wire it into
 *    ConfigController.GetFeatureFlags (call it + add to the Flags dictionary).
 * 2. Add an entry here with the SAME settingKey string used in FeatureFlagRegistry.
 * 3. Client — add the key to CLIENT_FEATURE_FLAG_KEYS and a useXEnabled() hook in
 *    wi-cloud-client/src/hooks/useFeatureFlags.ts, then gate the UI with it.
 * No seed data or migration needed — an admin creates the row from this tab
 * ("Create setting"), and it defaults to disabled until they do.
 */
export const FEATURE_FLAGS = [
  {
    type: "DomainRegistrationEnabled",
    key: "DomainRegistrationEnabled",
    settingKey: "domain_registration_enabled",
    label: "Domain Registration",
    description:
      "Shows/hides the Domains feature for all clients: sidebar group, /domains routes, and the VM-wizard domain bundle.",
    offConsequence:
      "Domains menu, /domains pages, and the wizard domain bundle become hidden for ALL clients (within ~6 minutes).",
  },
  {
    type: "DatabaseManagementEnabled",
    key: "DatabaseManagementEnabled",
    settingKey: "database_management_enabled",
    label: "Database Management",
    description: "Shows/hides the Databases sidebar nav item for all clients.",
    offConsequence:
      "The Databases nav item becomes hidden for ALL clients (within ~6 minutes).",
  },
  {
    type: "ZoneDisplayEnabled",
    key: "ZoneDisplayEnabled",
    settingKey: "zone_display_enabled",
    label: "Zone Display",
    description:
      "Shows/hides the zone/region info on VM detail (hero, overview, and network table) for all clients.",
    offConsequence:
      "Zone/region info becomes hidden on VM detail for ALL clients (within ~6 minutes).",
  },
  {
    type: "DnsManagementEnabled",
    key: "DnsManagementEnabled",
    settingKey: "dns_management_enabled",
    label: "DNS Management",
    description:
      "Shows/hides the DNS management section on VM detail for all clients.",
    offConsequence:
      "The DNS management section becomes hidden on VM detail for ALL clients (within ~6 minutes).",
  },
  {
    type: "ObjectStorageEnabled",
    key: "ObjectStorageEnabled",
    settingKey: "object_storage_enabled",
    label: "Object Storage",
    description: "Shows/hides the Object Storage sidebar menu for all clients.",
    offConsequence:
      "The Object Storage menu becomes hidden for ALL clients (within ~6 minutes).",
  },
  {
    type: "FirewallEnabled",
    key: "FirewallEnabled",
    settingKey: "firewall_enabled",
    label: "Firewall",
    description: "Shows/hides the Firewall sidebar menu for all clients.",
    offConsequence:
      "The Firewall menu becomes hidden for ALL clients (within ~6 minutes).",
  },
  {
    type: "MonitoringEnabled",
    key: "MonitoringEnabled",
    settingKey: "monitoring_enabled",
    label: "Monitoring",
    description: "Shows/hides the Monitoring sidebar menu for all clients.",
    offConsequence:
      "The Monitoring menu becomes hidden for ALL clients (within ~6 minutes).",
  },
  {
    type: "SupportWidgetEnabled",
    key: "SupportWidgetEnabled",
    settingKey: "support_widget_enabled",
    label: "Support Widget",
    description:
      "Shows/hides the floating contact launcher (Messenger, Zalo, hotline) on every client page. The channels themselves stay configured in the deployment's runtime config.",
    offConsequence:
      "The contact launcher becomes hidden for ALL clients (within ~6 minutes). No channel configuration is lost.",
  },
  {
    type: "VmDeletionEnabled",
    key: "VmDeletionEnabled",
    settingKey: "vm_deletion_enabled",
    label: "VM Deletion",
    description:
      "Lets customers delete a virtual machine from the client console (the list's row menu and the machine's own page). Enforced in the API — the client delete endpoint refuses a VM while it is off — not just hidden. Disks, networks and this admin console are not affected.",
    offConsequence:
      "Delete disappears from the client console for ALL clients (within ~6 minutes) and the client API refuses to delete a VM (within ~1 minute). Machines already deleted stay in the Pending-deletion tab and can still be restored.",
  },
  {
    type: "VmSuspendEnabled",
    key: "VmSuspendEnabled",
    settingKey: "vm_suspend_enabled",
    label: "VM Suspend / Resume",
    description:
      "Lets customers suspend and resume a virtual machine from the client console (the list's row menu and the machine's own page) — one flag for both actions. Enforced in the API — the client suspend and resume endpoints refuse while it is off — not just hidden. Start, the expiry suspension, the resume after a renewal and this admin console are not affected.",
    offConsequence:
      "Suspend and Resume disappear from the client console for ALL clients (within ~6 minutes) and the client API refuses both (within ~1 minute). Machines already suspended stay suspended; their owners bring them back with Start.",
  },
  {
    type: "AgreementSignupCommitmentEnabled",
    key: "AgreementSignupCommitmentEnabled",
    settingKey: "agreement_signup_commitment_enabled",
    label: "Commitment — After Sign-up",
    description:
      "Asks every tenant to read and accept the sign-up commitment before they can use the console. Independent of the machine-creation commitment. Not UI-level: while off, nothing is shown and no acceptance is recorded.",
    offConsequence:
      "No client is asked for the sign-up commitment (within ~6 minutes). Acceptances already recorded are kept.",
  },
  {
    type: "AgreementVmCreationCommitmentEnabled",
    key: "AgreementVmCreationCommitmentEnabled",
    settingKey: "agreement_vm_creation_commitment_enabled",
    label: "Commitment — Before Creating a Machine",
    description:
      "Asks every tenant to accept the machine-creation commitment before their first machine. Enforced in the API — the order and trial endpoints refuse while it is outstanding — not just in the wizard.",
    offConsequence:
      "Machine creation stops being gated by this commitment (within ~6 minutes). Acceptances already recorded are kept.",
  },
  {
    type: "EInvoiceEnabled",
    key: "EInvoiceEnabled",
    settingKey: "e_invoice_enabled",
    label: "E-invoice",
    description:
      "Shows/hides the electronic invoice menu and page in client billing.",
    offConsequence:
      "The e-invoice menu and page become unavailable after clients refresh their configuration. Existing invoices are kept.",
  },
  {
    type: "VmUpgradeEnabled",
    key: "VmUpgradeEnabled",
    settingKey: "vm_upgrade_enabled",
    label: "VM Upgrade",
    description:
      "Lets customers upgrade a machine's CPU, RAM and disks themselves from the client console. Enforced in the API — the upgrade quote and order refuse while it is off — not just hidden. The admin console's Change configuration is not affected.",
    offConsequence:
      "The Upgrade entry becomes hidden for ALL clients and the upgrade endpoints refuse (within ~6 minutes). Upgrade orders already created stay payable.",
  },
  {
    type: "VmReinstallConfigCheckEnabled",
    key: "VmReinstallConfigCheckEnabled",
    settingKey: "vm_reinstall_config_check_enabled",
    label: "VM Reinstall — Configuration Check",
    description:
      "Refuses an in-place reinstall when the machine's vCPU, memory, total disk or OS disk type at the infrastructure differs from what WiCloud recorded (RESOURCE_SPEC_MISMATCH). Enforced in the API for the client and the admin console alike.",
    offConsequence:
      "A reinstall sends the recorded configuration as it is and the infrastructure rebuilds the machine with it, so a machine changed outside WiCloud is resized back to its record (within ~1 minute). Nothing is compared and nothing is logged.",
  },
] as const;

/**
 * Mirror the backend FeatureFlagService.GetBooleanFlagAsync semantics exactly:
 * - missing row  -> false (safe default-OFF; configuration is opt-in)
 * - IsActive=false -> false (P0: an inactive row is disabled, NOT default-ON)
 * - otherwise   -> empty/"true" (trimmed, case-insensitive) is ON; else OFF (P1: trim)
 */
export function isFeatureFlagEnabled(row?: PlatformSettingListDto): boolean {
  if (!row) return false;
  if (!row.isActive) return false;
  const trimmed = row.value?.trim().toLowerCase() ?? "";
  return trimmed === "" || trimmed === "true";
}

export interface PlatformSettingListDto {
  id: string;
  type: SettingType;
  key: string;
  value: string;
  description?: string;
  isActive: boolean;
  tenantId?: string;
  region?: string;
  createdAt: string;
  modifiedAt?: string;
}

export type PlatformSettingDetailDto = PlatformSettingListDto;

export interface CreatePlatformSettingRequest {
  type: string;
  key: string;
  value: string;
  description?: string;
  tenantId?: string;
  region?: string;
}

export interface UpdatePlatformSettingRequest {
  value?: string;
  description?: string;
  isActive?: boolean;
}

export interface PlatformSettingFilters {
  type?: string;
  key?: string;
  tenantId?: string;
  region?: string;
  page?: number;
  limit?: number;
}

export const SENSITIVE_TYPES = new Set(["SmtpPassword"]);

/** The reinstall section of the VM settings. Mirrors backend VmReinstallSettingsDto. */
export interface VmReinstallSettingsDto {
  /** Whether the two limits are switched on. They keep their values while it is off. */
  limitEnabled: boolean;
  /** Reinstalls a customer may make on a trial VM over the whole trial. */
  trialLimit: number;
  /** Reinstalls a customer may make on a paid VM in each month of its billing period. */
  paidLimitPerMonth: number;
}

/**
 * The platform-wide VM settings, one property per section. Mirrors backend VmSettingsDto. A new VM
 * setting is a new field in its section (or a new section), never a new setting type.
 */
export interface VmSettingsDto {
  reinstall: VmReinstallSettingsDto;
}

/** Each section is saved on its own: one left out keeps the value it has on the server. */
export type UpdateVmSettingsRequest = Partial<VmSettingsDto>;
